import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
const airChanges = [{
  sector: 'Offices',
  ach: '6 ACH'
}, {
  sector: 'Retail',
  ach: '8 ACH'
}, {
  sector: 'Restaurants',
  ach: '10 ACH'
}, {
  sector: 'Gyms',
  ach: '12 ACH'
}, {
  sector: 'Commercial kitchens',
  ach: '12+ ACH'
}];
export function Ventilation() {
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-1373">
      {/* Hero */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-1374">
        <div className="container mx-auto px-6" data-id="element-1375">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-1376">
            <div className="lg:col-span-7" data-id="element-1377">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-1378">
                Services / Ventilation
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark" data-id="element-1379">
                Commercial ventilation designed for your sector
              </motion.h1>
            </div>
            <div className="lg:col-span-5 lg:pt-12" data-id="element-1380">
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1381">
                The right air, in the right volume, for your building type. We
                design and install ventilation systems that meet CIBSE
                guidelines and Building Regulations.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Why ventilation matters */}
      <section className="bg-white py-24" data-id="element-1382">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-1383">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-8" data-id="element-1384">
            Why air quality matters commercially
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="space-y-6 text-lg text-primary-dark/70 leading-relaxed" data-id="element-1385">
            <p data-id="element-1386">
              Proper ventilation isn't just about comfort; it's a critical
              business requirement. In offices, optimal air quality directly
              correlates with increased productivity and reduced staff sickness.
              In retail and hospitality, it dictates how long customers stay and
              how comfortable they feel.
            </p>
            <p data-id="element-1387">
              Beyond comfort, strict compliance with Building Regulations and
              Health & Safety laws means your ventilation system must be
              engineered to deliver specific air change rates based on your
              sector's demands.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Air changes by sector */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-1388">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-1389">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-1390">
            Required air changes per hour
          </motion.h2>
          <div className="grid md:grid-cols-5 gap-4 mb-8" data-id="element-1391">
            {airChanges.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-2xl text-center shadow-sm border border-primary-dark/5" data-id="element-1392">
                <div className="text-2xl font-medium text-accent mb-2" data-id="element-1393">
                  {item.ach}
                </div>
                <div className="text-sm font-medium text-primary-dark/70" data-id="element-1394">
                  {item.sector}
                </div>
              </motion.div>)}
          </div>
          <p className="text-center text-sm text-primary-dark/50" data-id="element-1395">
            *Guidelines based on CIBSE Guide B2 standards.
          </p>
        </div>
      </section>

      {/* HRV section */}
      <section className="bg-white py-24" data-id="element-1396">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-1397">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-6" data-id="element-1398">
            Heat recovery ventilation (HRV)
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1399">
            Extracting stale air shouldn't mean throwing away your heating
            budget. Our Heat Recovery Ventilation (HRV) systems recover up to
            90% of the heat from extracted air, transferring it to the incoming
            fresh air. This provides exceptional air quality while drastically
            reducing your energy bills.
          </motion.p>
        </div>
      </section>

      {/* Compliance */}
      <section className="bg-[#D4DDE8] py-24" data-id="element-1400">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-1401">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-serif mb-6" data-id="element-1402">
            Ventilation compliance
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-primary-dark/70 leading-relaxed" data-id="element-1403">
            Navigating Building Regulations Part F and CIBSE guidelines can be
            complex. AccuTemp engineers design, install, and commission your
            ventilation systems to ensure absolute compliance, providing you
            with the necessary certification and peace of mind.
          </motion.p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary-dark py-24 text-center" data-id="element-1404">
        <div className="container mx-auto px-6" data-id="element-1405">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-2xl mx-auto" data-id="element-1406">
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-10" data-id="element-1407">
              Discuss your ventilation requirements
            </h2>
            <Link to="/#contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-1408">
              Contact our team
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-1409" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>;
}